import type { CalcReport, ReportBlock } from "../api";

/** Позиции чисел на диаграмме матрицы (viewBox 546×546). Схема — восьмиконечная: квадрат и ромб вокруг центра. */
const MATRIX_POS: Record<string, [number, number]> = {
  A: [53, 274], B: [273, 54], C: [493, 274], D: [273, 495], E: [273, 274],
  AB: [117, 119], BC: [429, 119], CD: [429, 430], DA: [117, 430],
  top_out: [273, 100], top_mid: [273, 136], top_in: [273, 199],
  left_out: [99, 274], left_mid: [134, 274], left_in: [198, 274],
  right_out: [447, 274], right_mid: [412, 274],
  bottom_out: [273, 448], bottom_mid: [273, 413],
  ul_out: [150, 151], ul_mid: [175, 176], ur_out: [396, 151], ur_mid: [371, 176],
  ll_out: [150, 398], ll_mid: [175, 373], lr_out: [396, 398], lr_mid: [371, 373],
  diagSum: [317, 274], diagSumC: [348, 274],
  moneyA: [370, 316], moneyKey: [342, 344], moneyB: [315, 372],
};
const BIG = new Set(["A", "B", "C", "D", "E", "AB", "BC", "CD", "DA"]);
const MID = new Set(["top_out", "left_out", "right_out", "bottom_out", "ul_out", "ur_out", "ll_out", "lr_out"]);

function MatrixDiagram({ points }: { points: Record<string, number> }) {
  return (
    <svg className="rp-matrix" viewBox="0 0 546 546" role="img" aria-label="Диаграмма матрицы судьбы">
      <g fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.55">
        <rect x="53" y="54" width="440" height="441" />
        <polygon points="273,54 493,274 273,495 53,274" />
        <line x1="53" y1="274" x2="493" y2="274" />
        <line x1="273" y1="54" x2="273" y2="495" />
        <line x1="117" y1="119" x2="429" y2="430" />
        <line x1="429" y1="119" x2="117" y2="430" />
      </g>
      {Object.entries(MATRIX_POS).map(([k, [x, y]]) => {
        const v = points[k];
        if (v === undefined) return null;
        const r = BIG.has(k) ? 30 : MID.has(k) ? 24 : 19;
        return (
          <g key={k}>
            <circle cx={x} cy={y} r={r} className={BIG.has(k) ? "rp-mx-big" : "rp-mx-dot"} />
            <text x={x} y={y} textAnchor="middle" dominantBaseline="central" className="rp-mx-num" fontSize={r > 25 ? 26 : r > 20 ? 22 : 17}>
              {v}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function Block({ b }: { b: ReportBlock }) {
  switch (b.kind) {
    case "lead":
      return <p className="rp-lead">{b.text}</p>;
    case "note":
      return <p className="rp-note">{b.text}</p>;
    case "facts":
      return (
        <section className="rp-sec">
          {b.title && <h3>{b.title}</h3>}
          <dl className="rp-facts">
            {b.items.map((it, i) => (
              <div key={i} className="rp-fact">
                <dt>{it.label}</dt>
                <dd>
                  <b>{it.value}</b>
                  {it.note && <span>{it.note}</span>}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      );
    case "grid3":
      return (
        <section className="rp-sec">
          {b.title && <h3>{b.title}</h3>}
          <div className="rp-grid3">
            {b.cells.map((c, i) => (
              <div key={i} className={`rp-cell${c.value === "—" ? " empty" : ""}`}>
                {c.caption && <em>{c.caption}</em>}
                <b>{c.value}</b>
                <span>{c.label}</span>
              </div>
            ))}
          </div>
        </section>
      );
    case "cards":
      return (
        <section className="rp-sec">
          {b.title && <h3>{b.title}</h3>}
          {b.cards.map((c, i) => (
            <article key={i} className={`rp-card ${c.tone ?? "neutral"}`}>
              <header>
                <h4>{c.title}</h4>
                {c.badge && <span className="rp-badge">{c.badge}</span>}
              </header>
              <p>{c.text}</p>
            </article>
          ))}
        </section>
      );
    case "pillars":
      return (
        <section className="rp-sec">
          {b.title && <h3>{b.title}</h3>}
          <div className="rp-pillars" style={{ gridTemplateColumns: `repeat(${b.pillars.length}, 1fr)` }}>
            {b.pillars.map((p, i) => (
              <div key={i} className="rp-pillar">
                <small>{p.label}</small>
                <div className="rp-hanzi">{p.top}</div>
                <span>{p.topNote}</span>
                <div className="rp-hanzi">{p.bottom}</div>
                <span>{p.bottomNote}</span>
              </div>
            ))}
          </div>
          {b.pillars.map((p, i) => (
            <p key={i} className="rp-pnote">
              <b>{p.label}.</b> {p.extra}
            </p>
          ))}
        </section>
      );
    case "matrix":
      return (
        <section className="rp-sec">
          {b.title && <h3>{b.title}</h3>}
          <MatrixDiagram points={b.points} />
        </section>
      );
  }
}

/** Рисует отчёт калькулятора: сервер присылает готовые блоки, приложение ничего не считает. */
export function ReportView({ report }: { report: CalcReport }) {
  return (
    <div className="rp">
      {report.subtitle && <p className="rp-sub">{report.subtitle}</p>}
      {report.blocks.map((b, i) => (
        <Block key={i} b={b} />
      ))}
    </div>
  );
}
